import { Component, Input, SimpleChanges } from '@angular/core';

@Component({
  selector: 'app-common-csv',
  templateUrl: './common-csv.component.html',
  styleUrls: ['./common-csv.component.css']
})
export class CommonCsvComponent {
  @Input() data: any[] = [];
  @Input() fileName: string = ''; 
  @Input() columnsToExport: string[] = []; 
  @Input() allColumns: {id?:any, key: string, header: string }[] = [];
  @Input() allRecords: any;
  @Input() exportData: any[] = [];
  @Input() columns: any[] = [];
  @Input() includeLineItems: Boolean = false;
  @Input() customHeaders: { label: string; startCol: number; endCol: number }[] = [];
  columnHeaderMap: { [identifier: string]: string } = {};

  ngOnChanges(changes: SimpleChanges): void {
      // Regenerate header map when allColumns change
      if (changes['allColumns']) {
        this.columnHeaderMap = this.allColumns.reduce((acc, col) => {
          const identifier = col.id ?? col.key;
          acc[identifier] = col.header;
          return acc;
        }, {} as { [key: string]: string });
      }
  }

  exportToCsv(): void {
    if (!this.exportData || this.exportData.length === 0) {
      alert('No data available to export.');
      return;
    }
  
    // Identifiers for export, based on columns or allColumns
    const exportIdentifiers = this.columns.length > 0
      ? this.columns.map(col => col.id ?? col.key)
      : this.allColumns.map(col => col.id ?? col.key);
  
    // Prepare processed data
    const processedData = this.exportData.flatMap(row => {
      const baseRow = this.processRow(row, exportIdentifiers);
  
      // If line items are not included, return only the base row
      if (!this.includeLineItems) return [baseRow];
  
      const lineItems = Array.isArray(row.invoiceLineItems) ? row.invoiceLineItems : [];
      if (lineItems.length === 0) return [baseRow];
  
      // Process line items
      return lineItems.map((lineItem: any) => ({
        ...baseRow,
        VendorInvoiceNumber: lineItem.invoiceNumber,
        Amount: lineItem.amount,
        BudgetAmount: lineItem.budgetAmount,
        BudgetStatus: lineItem.budgetStatus,
        Expense: lineItem.expense,
        PaymentStatus: lineItem.paymentStatus,
        ReferenceNo: lineItem?.referenceNumber,
        InvoiceDate: lineItem.invoiceDate
      }));
    });
  
    // Dynamically collect all unique keys from the processed data for headers
    const allKeys = new Set<string>();
    processedData.forEach(row => {
      Object.keys(row).forEach(key => allKeys.add(key));
    });
  
    // Get export keys, which are the unique fields from both base row and line items
    const exportKeys = Array.from(allKeys);
  
    // Map these keys to their respective headers
    const headers = exportKeys.map(key => this.columnHeaderMap[key] || key);
  
    // Prepare CSV content by mapping each row to the export keys
    // const csvRows = [
    //   headers.join(','), // First row contains the headers
    //   ...processedData.map(row =>
    //     exportKeys.map(key => `"${(row[key] ?? '').toString().replace(/"/g, '""')}"`).join(',')
    //   )
    // ];
  
    // // Create CSV content
    // const csvContent = csvRows.join('\n');
    // const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    // const url = URL.createObjectURL(blob);
  
    // // Trigger the CSV file download
    // const link = document.createElement('a');
    // link.setAttribute('href', url);
    // link.setAttribute('download', `${this.fileName || 'Export'}.csv`);
    // link.click();
    const csvRows = [];
    if (this.customHeaders && this.customHeaders.length > 0) {
      // Add top header row for custom headers
      const topHeaderRow = new Array(headers.length).fill('');
      this.customHeaders.forEach(header => {
        topHeaderRow[header.startCol - 1] = header.label;
      });
      csvRows.push(topHeaderRow.map(val => `"${val.toString().replace(/"/g, '""')}"`).join(','));
    }

    // Add column header row
    csvRows.push(headers.map(header => `"${header.replace(/"/g, '""')}"`).join(','));

    // Add data rows
    csvRows.push(...processedData.map(row =>
      exportKeys.map(key => `"${(row[key] ?? '').toString().replace(/"/g, '""')}"`).join(',')
    ));

    // Create and download CSV
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${this.fileName || 'Export'}.csv`);
    link.click();
    URL.revokeObjectURL(url);
  }
  
  private processRow(row: any, exportIdentifiers: (number | string)[]): any {
    const exportRow: any = {};
    exportIdentifiers.forEach(identifier => {
      const column = this.allColumns.find(col => (col.id ?? col.key) === identifier);
      if (!column) return;
  
      const key = column.key;
      switch (key) {
        case 'status':
          exportRow[key] = row[key] === 1 ? 'Active' : 'Inactive';
          break;
        case 'expenseAssociated':
          exportRow[key] = row[key]?.map((item: any) => item.expense)?.join(', ') || '';
          break;
        case 'approvalUser':
          exportRow[key] = row[key]
            ?.map((item: any) => `${item.name || ''} ${item.approvalStatus ? `(${item.approvalStatus})` : ''}`.trim())
            ?.join(', ') || '';
          break;
        case 'allApprovalsUser':
          exportRow[key] = row[key]
            ?.map((item: any) => {
              const statusName = this.getApprovalStatusName(item.approvalStatus);
              const dateFormatted = item.updatedAt
                ? new Date(item.updatedAt).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', 
                                                                    hour: '2-digit', minute: '2-digit', second: '2-digit' })
                : '';
              let approvalPart = '';
              if (item.approvalStatus === 2) {
                approvalPart = ` (${statusName}) - ${dateFormatted}`;
              }
              const remarksPart = item?.remarks ? ' - ' + item.remarks : '';
              return `(${item?.sequence}) ${item.name || ''}${approvalPart}${remarksPart}`.trim();
            })
            ?.join(', ') || '';
          break;

          case 'currentApprovalUser':
          exportRow[key] =
          row[key]
            ?.filter((item: any) => item?.approvalStatus == 1 && item?.viewStatus == 1)
            ?.map((item: any) =>
              `${item.name || ''} ${
                item.approvalStatus ? `` : ''
              }`.trim()
            )
            ?.join(' ') || '';

          break;
        case 'invoiceLineItems':
          exportRow[key] = row[key]?.map((item: any) => item.expense)?.join(', ') || '';
          break;
        case 'venderInvoiceNumber':
          exportRow[key] = row[key]?.map((item: any) => item.invoiceNumber)?.join(', ') || '';
          break;
        case 'expenseModeMapping':
          exportRow[key] = row[key]?.map((item: any) => item.expenseMode)?.join(', ') || '';
          break;
        case 'attributes':
          exportRow[key] = row[key]?.map((item: any) => item.quickCodeName)?.join(', ') || '';
          break;
        case 'revenueAttributes':
          exportRow[key] = row[key]?.map((item: any) => item.attributeValue)?.join(', ') || '';
          break;
        case 'approvalHierarchy':
          row[key] = this.sortApproval(row[key])
          exportRow[key] =
            row[key]
              ?.map((item: any,index:any) =>
                `(${index + 1}) ${item.designation || ''} ${
                  item.condition ? `(${item.condition})` : ''
                }`.trim()
              )
              ?.join(', ') || '';
          break;
        case 'userRole':
          exportRow[key] = row[key]?.map((item: any) => item.roleDescription)?.join(', ') || '';
          break;
        case 'warehouseList':
          exportRow[key] = row[key]
            ?.map((item: any) => {
              const client = item.clientName || '';
              const warehouse = item.warehouseName || '';
              return client && warehouse ? `${client} - ${warehouse}` : client || warehouse;
            })?.join(', ') || '';
          break;
        default:
          const value = row[key];
          if (Array.isArray(value)) {
            exportRow[key] = value.join(', ');
          } else if (typeof value === 'object' && value !== null) {
            exportRow[key] = JSON.stringify(value);
          } else {
            exportRow[key] = value ?? '';
          }
      }
    });
    return exportRow;
  }

  sortApproval(data:any){
    return data.sort((a:any, b:any) => a.sequence - b.sequence);
  }

  private getApprovalStatusName(status: number | undefined): string {
    switch (status) {
      case 1:
        return 'Pending';
      case 2:
        return 'Approved';
      case 9:
        return 'Partially Approved';
      case 3:
        return 'Rejected';
      case 4:
        return 'Commented';
      default:
        return '';
    }
  }
  
}
